// React hook that returns the current user's capability predicate.
//
// Slice RP-2b-i / SC-2 of docs/planning/firefly_role_context_presentation.md
// §10–§11. Composes:
//
//   - explicit user overrides from localStorage (`capabilityStorage.ts`),
//   - DB-backed admin grants (`org_member_capabilities` via RPC),
//   - role-derived defaults (`capabilityResolution.ts`).
//
// Precedence: localStorage override > DB grant > role-derived default.
// Checklist + ads-activity dock gates consume this predicate.

import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import type { HasCapabilityFn } from "@/lib/capabilities/capabilities";
import {
  getCapabilityOverrides,
  subscribeCapabilityOverrides,
  type CapabilityOverrides,
} from "@/lib/capabilities/capabilityStorage";
import { resolveCapabilities } from "@/lib/capabilities/capabilityResolution";
import { fetchMyMemberCapabilityOverrides } from "@/lib/capabilities/memberCapabilitiesClient";

/**
 * Returns the current user's `HasCapabilityFn`. The reference is
 * stable across renders for a given (overrides, dbGrants, hasRole) pair.
 */
export function useUserCapabilities(): HasCapabilityFn {
  const auth = useAuth();
  const [overrides, setOverrides] = useState<CapabilityOverrides>(
    getCapabilityOverrides,
  );
  const [dbGrants, setDbGrants] = useState<CapabilityOverrides>({});

  useEffect(() => {
    const unsubscribe = subscribeCapabilityOverrides(setOverrides);
    setOverrides(getCapabilityOverrides());
    return unsubscribe;
  }, []);

  const organizationId =
    auth.status === "ready" ? (auth.activeOrganizationId ?? null) : null;
  const userId =
    auth.status === "ready"
      ? (auth.session?.user?.id ?? auth.profile?.id ?? null)
      : null;

  useEffect(() => {
    if (!organizationId || !userId) {
      setDbGrants({});
      return;
    }
    let cancelled = false;
    void (async () => {
      const next = await fetchMyMemberCapabilityOverrides(organizationId);
      if (!cancelled) setDbGrants(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId, userId]);

  return useMemo<HasCapabilityFn>(
    () =>
      resolveCapabilities(
        overrides,
        (...roles) => auth.hasRole(...roles),
        dbGrants,
      ),
    [overrides, dbGrants, auth],
  );
}
